import { useMemo } from 'react'
import { create } from '@bufbuild/protobuf'
import { timestampFromDate } from '@bufbuild/protobuf/wkt'
import { Skeleton } from 'skullet/react'
import { Link } from '@tanstack/react-router'
import { KanbanBoard } from '@/components/shared/kanban'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { X } from 'lucide-react'
import { TaskStatusEnum, ParentTypeEnum } from '@/gen/cortado/v1/settings_pb'
import { TaskLiteSchema } from '@/gen/cortado/v1/actions_pb'
import type { BadgeVariant } from '@/lib/enum-utils'
import { formatTimestamp, timestampToDateOnly } from '@/lib/date-utils'
import { formatFullName } from '@/lib/utils'
import { isDateInCustomRange } from '@/lib/date-filter-utils'
import { TasksKanbanFilterDropdown } from './tasks-kanban-filter-dropdown'
import {
  useTasksSelectedStatuses,
  useTasksSelectedAssignees,
  useTasksDueDateRange,
  useTasksSearchQuery,
  tasksFiltersActions,
} from '@/stores/tasks-filters.store'
import { mapTaskLiteToKanbanItem, type TaskKanbanItem } from './types'

type TaskColumn = {
  status: TaskStatusEnum
  label: string
  color: string
}

interface TasksKanbanProps {
  data: TaskKanbanItem[]
  columns: TaskColumn[]
  onItemMove?: (itemId: string, newStatus: TaskStatusEnum, oldStatus: TaskStatusEnum) => Promise<void>
  onCardClick?: (taskId: string, task: TaskKanbanItem) => void
  onCardHover?: (taskId: string, task: TaskKanbanItem) => void
  isLoading?: boolean
  toolbarRight?: React.ReactNode
}

const { TASK_STATUS_NOT_STARTED, TASK_STATUS_IN_PROGRESS, TASK_STATUS_COMPLETED } = TaskStatusEnum

const SAMPLE_BOARD: { items: TaskKanbanItem[]; columns: TaskColumn[] } = {
  columns: [
    { status: TASK_STATUS_NOT_STARTED, label: 'Not Started', color: 'bg-gray-400' },
    { status: TASK_STATUS_IN_PROGRESS, label: 'In Progress', color: 'bg-blue-400' },
    { status: TASK_STATUS_COMPLETED, label: 'Completed', color: 'bg-green-400' },
  ],
  items: [
    { taskStatus: TASK_STATUS_NOT_STARTED, title: 'Confirm final guest count' },
    { taskStatus: TASK_STATUS_NOT_STARTED, title: 'Send menu tasting invite' },
    { taskStatus: TASK_STATUS_NOT_STARTED, title: 'Book rental tables' },
    { taskStatus: TASK_STATUS_IN_PROGRESS, title: 'Draft run of show' },
    { taskStatus: TASK_STATUS_IN_PROGRESS, title: 'Collect deposit' },
    { taskStatus: TASK_STATUS_IN_PROGRESS, title: 'Order linens' },
    { taskStatus: TASK_STATUS_COMPLETED, title: 'Sign venue contract' },
    { taskStatus: TASK_STATUS_COMPLETED, title: 'Share floor plan' },
    { taskStatus: TASK_STATUS_COMPLETED, title: 'Schedule walkthrough' },
  ].map((task, i) =>
    mapTaskLiteToKanbanItem(
      create(TaskLiteSchema, {
        ...task,
        id: `sample-${i}`,
        assignedToFirstName: 'Maya',
        assignedToLastName: 'Chen',
        parentType: ParentTypeEnum.PARENT_TYPE_EVENT,
        parentName: 'Spring wedding',
        dueDate: timestampFromDate(new Date(2026, 5, 12 + i)),
      }),
    ),
  ),
}

const badgeVariantToTailwind = (variant: BadgeVariant): string => {
  switch (variant) {
    case 'default':
      return 'bg-gray-400'
    case 'secondary':
      return 'bg-gray-500'
    case 'success':
      return 'bg-green-400'
    case 'warning':
      return 'bg-yellow-400'
    case 'danger':
      return 'bg-red-400'
    case 'info':
      return 'bg-blue-400'
    case 'purple':
      return 'bg-purple-400'
    case 'pink':
      return 'bg-pink-400'
    case 'orange':
      return 'bg-orange-400'
    default:
      return 'bg-gray-400'
  }
}

/**
 * Get route and label for a task's parent entity
 */
function getParentRoute(parentType: ParentTypeEnum, parentId: string): { route: string; label: string } | null {
  switch (parentType) {
    case ParentTypeEnum.PARENT_TYPE_INQUIRY:
      return { route: `/leads/${parentId}`, label: 'Lead' }
    case ParentTypeEnum.PARENT_TYPE_EVENT:
      return { route: `/events/${parentId}`, label: 'Event' }
    case ParentTypeEnum.PARENT_TYPE_BOOKING:
      return { route: `/events/${parentId}`, label: 'Booking' }
    case ParentTypeEnum.PARENT_TYPE_CONTACT:
      return { route: `/crm/contacts/${parentId}`, label: 'Contact' }
    case ParentTypeEnum.PARENT_TYPE_INVOICE:
      return { route: `/invoices/${parentId}`, label: 'Invoice' }
    default:
      return null
  }
}

const renderTaskCard = (task: TaskKanbanItem, isLoading: boolean) => {
  const assigneeName = formatFullName(task.assignedToFirstName, task.assignedToLastName, 'Unassigned')
  // Due date is a date-only value (UTC-midnight on the wire); normalize to the
  // local calendar day so it renders and compares on the day the user picked.
  const dueDateOnly = timestampToDateOnly(task.dueDate)
  const dueDateStr = dueDateOnly ? formatTimestamp(dueDateOnly, 'MMM d') : null
  const today = new Date()
  today.setHours(0, 0, 0, 0)

  // Check if task is overdue
  const isOverdue = dueDateOnly && task.status !== TaskStatusEnum.TASK_STATUS_COMPLETED
    ? dueDateOnly < today
    : false

  return (
    <div className={`p-3 ${isLoading ? 'opacity-60' : ''}`}>
      <div className="space-y-2">
        {/* Title */}
        <h3 className="font-medium text-sm">{task.title}</h3>

        {/* Description (2-line clamp) */}
        {task.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {task.description}
          </p>
        )}

        {/* Linked entity */}
        {(() => {
          if (task.parentType === ParentTypeEnum.PARENT_TYPE_UNKNOWN || !task.parentId) {
            return <span className="text-muted-foreground">—</span>
          }
          const parent = getParentRoute(task.parentType, task.parentId)
          if (!parent) return <span className="text-muted-foreground">—</span>
          return (
            <Link
              to={parent.route}
              className="text-xs text-muted-foreground hover:text-foreground hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {parent.label} · {task.parentName || task.parentId.slice(0, 8)}
            </Link>
          )
        })()}

        {/* Footer with assignee and due date */}
        <div className="flex items-center justify-between text-xs text-muted-foreground pt-1">
          <span>{assigneeName}</span>
          {dueDateStr && (
            <Badge
              variant={isOverdue ? 'destructive' : 'secondary'}
              
>
              {dueDateStr}
            </Badge>
          )}
        </div>
      </div>
    </div>
  )
}

export function TasksKanban({
  data,
  columns,
  onItemMove,
  onCardClick,
  onCardHover,
  isLoading,
  toolbarRight,
}: TasksKanbanProps) {
  // Persisted filter state from store
  const selectedStatuses = useTasksSelectedStatuses()
  const selectedAssignees = useTasksSelectedAssignees()
  const dueDateRange = useTasksDueDateRange()
  const searchQuery = useTasksSearchQuery()

  // Filter tasks based on all filters (client-side filtering for search and assignee)
  const filteredData = useMemo(() => {
    return data.filter((task) => {
      // Search filter (title and description)
      if (searchQuery) {
        const query = searchQuery.toLowerCase()
        const titleMatch = task.title.toLowerCase().includes(query)
        const descMatch = task.description?.toLowerCase().includes(query)
        if (!titleMatch && !descMatch) return false
      }

      // Assignee filter (client-side since backend may not filter by multiple assignees)
      if (selectedAssignees.length > 0) {
        if (!selectedAssignees.includes(task.assignedTo)) return false
      }

      // Status filter is handled by backend, but also apply client-side for immediate feedback
      if (selectedStatuses.length > 0) {
        if (!selectedStatuses.includes(task.status)) return false
      }

      // Due date range filter (client-side)
      if (dueDateRange && (dueDateRange.from || dueDateRange.to)) {
        if (!task.dueDate) return false
        if (!isDateInCustomRange(task.dueDate, dueDateRange)) return false
      }

      return true
    })
  }, [data, searchQuery, selectedAssignees, selectedStatuses, dueDateRange])

  // Check if any filters are active
  const hasActiveFilters =
    searchQuery ||
    selectedStatuses.length > 0 ||
    selectedAssignees.length > 0 ||
    (dueDateRange?.from || dueDateRange?.to)

  const handleResetAll = () => {
    tasksFiltersActions.setSearchQuery('')
    tasksFiltersActions.resetFilters()
  }

  const handleCardClick = (taskId: string, task: TaskKanbanItem) => {
    onCardClick?.(taskId, task)
  }

  return (
    <div className="flex flex-col flex-1 min-h-0 space-y-4">
      {/* Search Toolbar */}
      <div className="flex items-center justify-between flex-shrink-0">
        <div className="flex flex-1 items-center space-x-2">
          <Input
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(event) => tasksFiltersActions.setSearchQuery(event.target.value)}
            className="h-8 w-[150px] lg:w-[250px]"
          />
          <TasksKanbanFilterDropdown
            selectedStatuses={selectedStatuses}
            onStatusesChange={tasksFiltersActions.setSelectedStatuses}
            selectedAssignees={selectedAssignees}
            onAssigneesChange={tasksFiltersActions.setSelectedAssignees}
            dueDateRange={dueDateRange}
            onDueDateRangeChange={tasksFiltersActions.setDueDateRange}
          />
          {hasActiveFilters && (
            <Button
              variant="ghost"
              onClick={handleResetAll}
              className="h-8 px-2 lg:px-3"
            >
              Reset
              <X />
            </Button>
          )}
        </div>
        {toolbarRight}
      </div>

      {/* Kanban Board */}
      <div className="flex-1 min-h-0">
        <Skeleton
          data={isLoading ? null : { items: filteredData, columns }}
          sample={SAMPLE_BOARD}
          id="tasks:board"
          label="Loading tasks"
        >
          {({ items, columns }) => (
            <KanbanBoard
              items={items}
              columns={columns}
              onItemMove={onItemMove}
              onCardClick={handleCardClick}
              onCardHover={onCardHover}
              renderCard={renderTaskCard}
            />
          )}
        </Skeleton>
      </div>
    </div>
  )
}

// Export utility function for use in parent component
export { badgeVariantToTailwind }
